import api from './api';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 180000;
const JOB_STATUS_PATH = '/text-improvement-jobs';

const JOB_STATUSES = new Set(['pending', 'processing', 'completed', 'failed']);

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function isTextFieldVersionPayload(data) {
  return Boolean(
    data &&
      typeof data === 'object' &&
      data.id &&
      data.resource_type &&
      data.field_name &&
      !data.job_id &&
      !data.status
  );
}

function assertJobStatusPayload(data, jobId) {
  if (isTextFieldVersionPayload(data)) {
    throw new Error(
      'A consulta do job de melhoria recebeu um registro de text-field-version. Reinicie a melhoria.'
    );
  }

  if (!data || typeof data.status !== 'string' || !JOB_STATUSES.has(data.status)) {
    const error = new Error(
      `Resposta inválida ao consultar o job de melhoria (${jobId}). Verifique se o backend assíncrono está atualizado.`
    );
    error.response = { data: { detail: error.message } };
    throw error;
  }
}

function extractJobIdFromStartResponse(data) {
  if (!data || typeof data !== 'object') {
    return null;
  }

  if (typeof data.job_id === 'string' && data.job_id.trim()) {
    return data.job_id;
  }

  return null;
}

export async function pollTextImprovementJob(jobId) {
  const startedAt = Date.now();

  while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
    let data;

    try {
      ({ data } = await api.get(`${JOB_STATUS_PATH}/${jobId}`));
    } catch (error) {
      if (error.response?.status === 404) {
        const notFoundError = new Error(
          'Job de melhoria não encontrado. Confirme que o backend e o worker Celery estão atualizados.'
        );
        notFoundError.response = error.response;
        throw notFoundError;
      }
      throw error;
    }

    assertJobStatusPayload(data, jobId);

    if (data.status === 'completed') {
      return {
        text: data.text,
        ready: data.ready,
        questions: Array.isArray(data.questions) ? data.questions : [],
        summary: data.summary,
      };
    }

    if (data.status === 'failed') {
      const error = new Error(
        data.error || 'Não foi possível melhorar o texto agora. Tente novamente.'
      );
      error.response = { data: { detail: data.error } };
      throw error;
    }

    await sleep(POLL_INTERVAL_MS);
  }

  const timeoutError = new Error(
    'A melhoria está demorando mais que o esperado. Tente novamente em instantes.'
  );
  timeoutError.response = {
    data: { detail: timeoutError.message },
  };
  throw timeoutError;
}

export async function submitAndPollTextImprovement(postRequest) {
  const response = await postRequest();
  const data = response.data ?? {};

  // Backend legado (síncrono): 200 + { text }
  if (response.status === 200 && typeof data.text === 'string') {
    return { text: data.text };
  }

  if (isTextFieldVersionPayload(data)) {
    throw new Error(
      'A API de melhoria retornou um registro de text-field-version em vez de um job assíncrono.'
    );
  }

  const jobId = extractJobIdFromStartResponse(data);

  if (!jobId) {
    const error = new Error(
      'Resposta inválida ao iniciar melhoria de texto (job_id ausente). Confirme que o backend assíncrono está em execução.'
    );
    error.response = { data: { detail: error.message } };
    throw error;
  }

  return pollTextImprovementJob(jobId);
}
